import {DoppeViewerAppClientServiceBase} from './doppe-viewer-app-client-service-base';
import type {Dictionary} from '@wix/devzai-utils-common';
import {assertDefined, ObservableValue} from '@wix/devzai-utils-common';
import type {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';
import {doppeActionIsLockActive} from '../../client-server-common/types/doppe-dto-action';
import {SupportedCookieNames} from '../../client-server-common';
import type {DoppePageLockSettings} from '../../client-server-common/types/doppe-page-lock-settings/doppe-page-lock-settings';
import {doppePageIsLockActive} from '../../client-server-common/types/doppe-page-lock-settings/doppe-page-lock-settings';

export class DoppeViewerAppLockService extends DoppeViewerAppClientServiceBase {

    protected readonly cachedLockedActionsObservable = new Map<DoppeDtoAction.Id, ObservableValue<boolean>>();
    protected readonly cachedLockedPagesObservable = new Map<string, ObservableValue<boolean>>();

    public getActionUnlockStateFromCookie(actionId: DoppeDtoAction.Id) {
        const unlockedCookieValue = this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor?.getItem<Dictionary<any>>(SupportedCookieNames.UnlockedActions);
        return unlockedCookieValue ? unlockedCookieValue[actionId] : undefined
    }

    public updateActionUnlockStateInCookie(actionId: DoppeDtoAction.Id, unlockedValue: any) {
        this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor?.updateItem(SupportedCookieNames.UnlockedActions, (value) => {
            if (value === undefined) {
                return {
                    [actionId]: unlockedValue
                };
            }
            else {
                return {
                    ...value,
                    [actionId]: unlockedValue
                };
            }
        })
    }

    /**
     * Records an action as unlocked and flips its lock observable to
     * "unlocked" without requiring a {@link DoppeDtoAction} instance.
     *
     * Idempotent: safe to call for an id that doesn't correspond to a locked
     * action on the current view (no observable cached → cookie write only;
     * the cookie entry is harmless for non-action ids).
     */
    public applyUnlockedActionStateById(actionId: DoppeDtoAction.Id, unlockedValue: any) {
        this.updateActionUnlockStateInCookie(actionId, unlockedValue);
        this.cachedLockedActionsObservable.get(actionId)?.setValue(false);
    }

    public getActionLockObservable(action: DoppeDtoAction<any>) {
        const isActionSettingsLocked = doppeActionIsLockActive(action);

        if (!isActionSettingsLocked) {
            if (this.cachedLockedActionsObservable.get(action.id)?.getValue() === false) {
                this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor?.updateItem(SupportedCookieNames.UnlockedActions, (value) => {
                    if (value === undefined) {
                        return undefined
                    }
                    else {
                        return {
                            ...value,
                            [action.id]: undefined
                        };
                    }
                })
            }

            this.cachedLockedActionsObservable.delete(action.id);

            return undefined
        }
        else {
            if (this.cachedLockedActionsObservable.has(action.id)) {
                return assertDefined(this.cachedLockedActionsObservable.get(action.id));
            }
            else {
                const booleanObservableValue = new ObservableValue<boolean>(isActionSettingsLocked);
                this.cachedLockedActionsObservable.set(action.id, booleanObservableValue);
                return booleanObservableValue;
            }
        }
    }

    public getPageUnlockStateFromCookie(pageId: string) {
        const unlockedCookieValue = this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor?.getItem<Dictionary<any>>(SupportedCookieNames.UnlockedActions);
        const pageCookieKey = `page_${pageId}`;
        return unlockedCookieValue ? unlockedCookieValue[pageCookieKey] : undefined;
    }

    public updatePageUnlockStateInCookie(pageId: string, unlockedValue: any) {
        const pageCookieKey = `page_${pageId}`;
        this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor?.updateItem(SupportedCookieNames.UnlockedActions, (value) => {
            if (value === undefined) {
                return {[pageCookieKey]: unlockedValue};
            } else {
                return {...value, [pageCookieKey]: unlockedValue};
            }
        });
    }

    /**
     * Page-lock counterpart of {@link applyUnlockedActionStateById}: records
     * the page as unlocked and flips its cached lock observable.
     *
     * Idempotent: safe to call for an id that doesn't correspond to a locked
     * page on the current view (no observable cached → cookie write only).
     */
    public applyUnlockedPageStateById(pageId: string, unlockedValue: any) {
        this.updatePageUnlockStateInCookie(pageId, unlockedValue);
        this.cachedLockedPagesObservable.get(pageId)?.setValue(false);
    }

    public resetPageLockState(pageId: string) {
        const pageCookieKey = `page_${pageId}`;
        this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor?.updateItem(SupportedCookieNames.UnlockedActions, (value) => {
            if (value === undefined) {
                return undefined;
            } else {
                return {...value, [pageCookieKey]: undefined};
            }
        });

        const observable = this.cachedLockedPagesObservable.get(pageId);
        if (observable) {
            observable.setValue(true);
        }
    }

    public getPageLockObservable(pageId: string, lockSettings: DoppePageLockSettings | undefined) {
        const isPageLocked = doppePageIsLockActive(lockSettings);

        if (!isPageLocked) {
            if (this.cachedLockedPagesObservable.get(pageId)?.getValue() === false) {
                const pageCookieKey = `page_${pageId}`;
                this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor?.updateItem(SupportedCookieNames.UnlockedActions, (value) => {
                    if (value === undefined) {
                        return undefined;
                    } else {
                        return {...value, [pageCookieKey]: undefined};
                    }
                });
            }

            this.cachedLockedPagesObservable.delete(pageId);
            return undefined;
        } else {
            if (this.cachedLockedPagesObservable.has(pageId)) {
                return assertDefined(this.cachedLockedPagesObservable.get(pageId));
            } else {
                const previouslyUnlocked = this.getPageUnlockStateFromCookie(pageId) !== undefined;
                const booleanObservableValue = new ObservableValue<boolean>(!previouslyUnlocked);
                this.cachedLockedPagesObservable.set(pageId, booleanObservableValue);
                return booleanObservableValue;
            }
        }
    }
}
